/**
 * AriCommandCenterDrawer — Global Ari surface = Unified Ari Chat Center.
 *
 * Mounted ONCE in App.js. Listens for `ari:open-command-center` (dispatched by
 * PortalBriefing + AriSuperAdminPresence + any briefing card on any portal).
 *
 * ONE Ari, persistent everywhere. The Chat tab is a full ChatGPT-like center:
 * conversation history + pinned chats + photo/PDF/document uploads, backed by
 * the shared `/api/ari/conversations` API (which delegates to the existing Ari
 * brain per portal — provider vision+governance, customer concierge, SA
 * platform Q&A).
 *
 * Tab visibility (owner directive):
 *   - Provider (admin/owner/business/business_owner/provider): Chat + Insights + Control Center
 *   - Super Admin: Chat + Insights + Control Center
 *   - Worker (technician/dispatcher/contractor/network_contractor/tools) & Customer: Chat ONLY
 *
 * DEFAULT TAB on open = 'chat'.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, X, Send, RefreshCw, Loader2, AlertTriangle, Lightbulb,
  MessageSquare, Settings as SettingsIcon, Shield, BarChart2, ChevronDown,
  Plus, Paperclip, Pin, PinOff, Trash2, Archive, FileText, ChevronLeft, ExternalLink, Pencil, Clock, Play, Pause,
  Camera, Image as ImageIcon, Mic, Volume2, Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AriBriefingCarousel from './AriBriefingCarousel';
import CopyMessageButton from './AriMessageActions';

const API = process.env.REACT_APP_BACKEND_URL;

const ALERT_RANK = { critical: 0, high: 1, medium: 2 };

// Provider roles that get Insights + Control Center.
const FULL_MODE_ROLES = new Set(['admin', 'owner', 'business_owner', 'business', 'provider']);

const SUGGESTION_QUESTIONS = [
  'What needs attention?', 'What happened today?', 'Which payments failed?', 'What jobs are unscheduled?',
];
const SUGGESTION_QUESTIONS_SA = [
  'What needs attention?', 'Are there urgent failures?', 'Which businesses look unhealthy?', 'Are autopilot rules firing as expected?',
];
const SUGGESTION_QUESTIONS_CUSTOMER = [
  'Request a quote', 'Request a service', 'What are my upcoming appointments?', 'Show me my recent invoices',
];
const SUGGESTION_QUESTIONS_WORKER = [
  'What are my jobs today?', 'What needs my attention?', 'Help me diagnose an issue', 'What parts do I need?',
];

const ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.heic,.pdf,.txt,.csv,.docx,.xlsx,image/*';
const MAX_FILES = 5;
const MAX_INPUT = 10000;

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const AriCommandCenterDrawer = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(null);
  const [tab, setTab] = useState('chat');
  const [initialData, setInitialData] = useState(null);
  const [customerToken, setCustomerToken] = useState(null);
  const [customerSlug, setCustomerSlug] = useState(null);
  const [saSessionId, setSaSessionId] = useState(null);

  // Insights state — briefing deck ALWAYS collapsed by default
  // (owner-locked 2026-07 carousel revamp).
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);

  // Control Center state
  const [autopilotSettings, setAutopilotSettings] = useState(null);
  const [autopilotLoading, setAutopilotLoading] = useState(false);
  const companyId = typeof window !== 'undefined' ? localStorage.getItem('company_id') : '';

  // Chat Center state
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showList, setShowList] = useState(false);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const autoGrowInput = (el) => { if (!el) return; el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; };

  // Identity mode drives auth for the conversations API.
  const identityKind = customerToken ? 'portal' : (saSessionId ? 'sa' : 'jwt');
  const isCustomer = role === 'customer';
  const isSuperAdmin = role === 'super_admin';
  const isProvider = FULL_MODE_ROLES.has(role);
  const showInsights = isProvider || isSuperAdmin;
  const showControl = isProvider || isSuperAdmin;
  const suggestionQuestions = isCustomer ? SUGGESTION_QUESTIONS_CUSTOMER
    : isSuperAdmin ? SUGGESTION_QUESTIONS_SA
    : isProvider ? SUGGESTION_QUESTIONS
    : SUGGESTION_QUESTIONS_WORKER;

  const authCfg = useCallback(() => {
    const cfg = {};
    if (identityKind === 'portal' && customerToken) cfg.headers = { Authorization: `Bearer ${customerToken}` };
    if (identityKind === 'sa' && saSessionId) cfg.headers = { 'X-SA-Session': saSessionId };
    return cfg;
  }, [identityKind, customerToken, saSessionId]);

  // Temporary workspace tip — armed by AriWorkspaceIntro (Skip / Build Profile);
  // removed FOREVER once the company applies its first Workspace Evolution change.
  const [showWorkspaceTip, setShowWorkspaceTip] = useState(false);
  useEffect(() => {
    if (!open || !isProvider || !companyId) { setShowWorkspaceTip(false); return; }
    const uid = localStorage.getItem('user_id');
    try {
      if (localStorage.getItem(`ari_workspace_tip_done:${uid}`) === '1') { setShowWorkspaceTip(false); return; }
      if (localStorage.getItem(`ari_workspace_tip_pending:${uid}`) !== '1') { setShowWorkspaceTip(false); return; }
    } catch { return; }
    axios.get(`${API}/api/ari/workspace/${companyId}/proposals?status=applied`, authCfg())
      .then((r) => {
        if ((r.data?.count || 0) > 0) {
          try {
            localStorage.setItem(`ari_workspace_tip_done:${uid}`, '1');
            localStorage.removeItem(`ari_workspace_tip_pending:${uid}`);
          } catch { /* */ }
          setShowWorkspaceTip(false);
        } else {
          setShowWorkspaceTip(true);
        }
      })
      .catch(() => setShowWorkspaceTip(true));
  }, [open, isProvider, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Conversation API ───
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/ari/conversations`, authCfg());
      setConversations(data.conversations || []);
    } catch { setConversations([]); }
  }, [authCfg]);

  const openConversation = useCallback(async (id) => {
    setShowList(false);
    try {
      const { data } = await axios.get(`${API}/api/ari/conversations/${id}`, authCfg());
      const c = data.conversation || {};
      setActiveId(id);
      setMessages((c.messages || []).map((m) => ({ id: m.id, role: m.role, content: m.content, attachments: m.attachments || [], edited: !!m.edited })));
    } catch { /* ignore */ }
  }, [authCfg]);

  const newChat = useCallback(() => { setActiveId(null); setMessages([]); setShowList(false); setPendingFiles([]); setQuestion(''); }, []);

  // ─── Universal Ari Automations (Super Admin Control Center) ───
  const [automations, setAutomations] = useState([]);
  const [autoBusy, setAutoBusy] = useState(null);
  const loadAutomations = useCallback(async () => {
    const sid = localStorage.getItem('super_admin_session');
    if (!sid) return;
    try {
      const { data } = await axios.get(`${API}/api/super-admin/ari/automations`, { headers: { 'X-SA-Session': sid } });
      setAutomations(data.automations || []);
    } catch { /* silent */ }
  }, []);
  const controlAutomation = async (id, action) => {
    const sid = localStorage.getItem('super_admin_session');
    if (!sid) return;
    setAutoBusy(id);
    try {
      await axios.post(`${API}/api/super-admin/ari/automations/${id}/control`, { session_id: sid, action });
      await loadAutomations();
    } catch { /* silent */ } finally { setAutoBusy(null); }
  };

  // ─── Edit a previous user message → delete old branch → regenerate ───
  // Doctrine 2026-09 (§4): Ari can launch the guided walkthrough on request.
  // Backend embeds [START_WALKTHROUGH]; strip it and fire the launch event.
  const handleAriTokens = (text) => {
    if (text && text.includes('[START_WALKTHROUGH]')) {
      try { window.dispatchEvent(new CustomEvent('ari-start-walkthrough')); } catch { /* noop */ }
      return text.replace('[START_WALKTHROUGH]', '').trim();
    }
    return text;
  };
  const [editingIdx, setEditingIdx] = useState(null);
  const [editText, setEditText] = useState('');
  const startEdit = (idx) => { setEditingIdx(idx); setEditText(messages[idx]?.content || ''); };
  const cancelEdit = () => { setEditingIdx(null); setEditText(''); };
  const saveEdit = async () => {
    const idx = editingIdx;
    const msg = messages[idx];
    const text = editText.trim();
    if (idx === null || !msg?.id || !text || sending || !activeId) return;
    setSending(true);
    setEditingIdx(null);
    // Optimistic: truncate the old branch, show edited prompt + thinking bubble.
    setMessages((m) => [...m.slice(0, idx),
      { ...msg, content: text, edited: true },
      { role: 'assistant', content: null, loading: true }]);
    try {
      const { data } = await axios.put(
        `${API}/api/ari/conversations/${activeId}/messages/${msg.id}`,
        { message: text }, authCfg());
      setMessages((m) => [...m.slice(0, idx),
        { id: data.user_message?.id, role: 'user', content: text, attachments: data.user_message?.attachments || [], edited: true },
        { id: data.assistant_message?.id, role: 'assistant', content: handleAriTokens(data.response) }]);
      loadConversations();
    } catch {
      setMessages((m) => {
        const c = [...m];
        if (c.length && c[c.length - 1].loading) c[c.length - 1] = { role: 'assistant', content: "I couldn't regenerate just now. Try again in a moment." };
        return c;
      });
    } finally { setSending(false); }
  };

  const pinToggle = async (id, pinned) => {
    try { await axios.patch(`${API}/api/ari/conversations/${id}`, { pinned: !pinned }, authCfg()); loadConversations(); } catch { /* ignore */ }
  };
  const archiveConv = async (id) => {
    try { await axios.patch(`${API}/api/ari/conversations/${id}`, { archived: true }, authCfg()); if (id === activeId) newChat(); loadConversations(); } catch { /* ignore */ }
  };
  const deleteConv = async (id) => {
    try { await axios.delete(`${API}/api/ari/conversations/${id}`, authCfg()); if (id === activeId) newChat(); loadConversations(); } catch { /* ignore */ }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setPendingFiles((p) => [...p, ...files].slice(0, MAX_FILES));
    if (e.target) e.target.value = '';
  };

  // ─── A1: Voice notes (STT) + spoken replies (TTS) ───
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const voiceModeRef = useRef(false);
  const ttsAudioRef = useRef(null);

  const toggleRecording = async () => {
    if (recording) { mediaRecorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'voice-note.webm');
          fd.append('company_id', localStorage.getItem('company_id') || '');
          fd.append('user_id', localStorage.getItem('user_id') || '');
          const { data } = await axios.post(`${API}/api/ari/voice/transcribe`, fd, authCfg());
          const text = (data.text || '').trim();
          if (text) {
            voiceModeRef.current = true;
            setQuestion((q) => ((q ? q + ' ' : '') + text).slice(0, MAX_INPUT));
            requestAnimationFrame(() => {
              if (inputRef.current) { autoGrowInput(inputRef.current); inputRef.current.focus(); }
            });
          } else {
            toast.warning("Didn't catch that — try again");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Transcription failed — try again');
        } finally { setTranscribing(false); }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error('Microphone unavailable — check browser permissions');
    }
  };

  const speakMessage = async (idx, text) => {
    if (speakingIdx === idx) {
      try { ttsAudioRef.current?.pause(); } catch { /* ignore */ }
      setSpeakingIdx(null);
      return;
    }
    try {
      setSpeakingIdx(idx);
      const { data } = await axios.post(`${API}/api/ari/tts`, {
        text: (text || '').slice(0, 500),
        company_id: localStorage.getItem('company_id') || '',
        user_id: localStorage.getItem('user_id') || '',
      }, authCfg());
      if (data.success && data.audio_base64) {
        try { ttsAudioRef.current?.pause(); } catch { /* ignore */ }
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        ttsAudioRef.current = audio;
        audio.onended = () => setSpeakingIdx(null);
        await audio.play();
      } else {
        setSpeakingIdx(null);
        toast.info(data.error || 'Voice playback is available on the Autopilot plan');
      }
    } catch (err) {
      setSpeakingIdx(null);
      toast.info(err?.response?.data?.detail || 'Voice playback unavailable right now');
    }
  };
  const removePendingFile = (idx) => setPendingFiles((p) => p.filter((_, i) => i !== idx));

  const uploadCfg = () => {
    const cfg = {};
    if (identityKind === 'portal' && customerToken) cfg.headers = { Authorization: `Bearer ${customerToken}` };
    if (identityKind === 'sa' && saSessionId) cfg.headers = { 'X-SA-Session': saSessionId };
    return cfg;
  };

  // CORR-E (Wave 3): advisory screen context — route + title + portal, plus an
  // entity ref when the current route exposes one. Advisory only, never auth.
  const buildPageContext = () => {
    try {
      const route = (window.location.pathname || '').slice(0, 200);
      const pc = {
        route,
        page_title: (document.title || '').slice(0, 120),
        portal: identityKind === 'sa' ? 'super_admin' : (role || 'provider'),
      };
      const m = route.match(/\/(invoices?|jobs?|customers?|estimates?|appointments?|quotes?)\/([A-Za-z0-9_-]{1,64})(?:\/|$)/);
      if (m) { pc.entity_type = m[1].replace(/s$/, ''); pc.entity_id = m[2]; }
      return pc;
    } catch { return null; }
  };

  const handleSend = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = question.trim();
    if ((!text && pendingFiles.length === 0) || sending) return;    if (tab === 'insights') setTab('chat');
    setSending(true);
    const filesToSend = pendingFiles;
    setQuestion(''); setPendingFiles([]);
    if (inputRef.current) inputRef.current.style.height = 'auto';
    try {
      let convId = activeId;
      if (!convId) {
        const { data } = await axios.post(`${API}/api/ari/conversations`, {}, authCfg());
        convId = data.conversation.id; setActiveId(convId);
      }
      // Upload attachments first.
      const attRefs = []; let imageData = null;
      for (const f of filesToSend) {
        const fd = new FormData(); fd.append('file', f);
        try {
          const { data } = await axios.post(`${API}/api/ari/conversations/${convId}/attachments`, fd, uploadCfg());
          attRefs.push(data.attachment);
          if (!imageData && data.attachment.kind === 'image') { try { imageData = await fileToDataURL(f); } catch { /* ignore */ } }
        } catch { /* skip a failed upload */ }
      }
      // Optimistic bubbles.
      setMessages((m) => [...m, { role: 'user', content: text, attachments: attRefs }, { role: 'assistant', content: null, loading: true }]);
      const { data } = await axios.post(
        `${API}/api/ari/conversations/${convId}/messages`,
        { message: text, image_data: imageData, attachments: attRefs, page_context: buildPageContext() },
        authCfg(),
      );
      setMessages((m) => {
        const c = [...m];
        if (c.length >= 2) c[c.length - 2] = { ...c[c.length - 2], id: data.user_message?.id };
        c[c.length - 1] = { id: data.assistant_message?.id, role: 'assistant', content: handleAriTokens(data.response) };
        return c;
      });
      // Voice-initiated sends get the reply read back aloud.
      if (voiceModeRef.current && data.response) {
        voiceModeRef.current = false;
        setMessages((m) => { speakMessage(m.length - 1, data.response); return m; });
      }
      loadConversations();
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 429
        ? "You're asking faster than I can think. Try again in a minute."
        : "I couldn't reach Ari just now. Try again in a moment.";
      setMessages((m) => { const c = [...m]; if (c.length && c[c.length - 1].loading) c[c.length - 1] = { role: 'assistant', content: msg }; else c.push({ role: 'assistant', content: msg }); return c; });
    } finally { setSending(false); }
  };

  // ─── Insights + Control (unchanged) ───
  const fetchBriefing = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      if (isSuperAdmin) {
        const { data } = await axios.get(`${API}/api/super-admin/ari/platform-health`, { headers: saSessionId ? { 'X-SA-Session': saSessionId } : {} });
        const p = data.platform || {}, w = data.week_metrics || {}, a = data.autopilot_adoption || {};
        const lines = [
          `${p.total_companies || 0} companies on the platform (${p.active_companies || 0} active).`,
          `${p.total_users || 0} registered users.`,
          `${p.total_jobs ?? w.jobs_total ?? 0} total jobs on the platform (${w.jobs_created || 0} created this week, ${w.jobs_completed || 0} completed).`,
          `${a.total_enabled || 0} companies on autopilot (${a.full_mode || 0} full, ${a.assist_mode || 0} assist).`,
          (data.issues && data.issues.length) ? `Issues: ${data.issues.join('; ')}` : 'No platform issues detected.',
        ];
        setBriefing({ briefing: lines.join('\n'), summary: { jobs_created_24h: p.total_jobs ?? w.jobs_total ?? 0 } });
      } else {
        const { data } = await axios.get(`${API}/api/ari/insights/briefing${force ? '?force_refresh=true' : ''}`);
        setBriefing(data);
      }
    } catch { setBriefing({ briefing: "Ari isn't available right now. Try again in a moment." }); }
    finally { setLoading(false); setRefreshing(false); }
  }, [isSuperAdmin, saSessionId]);

  const fetchAutopilot = useCallback(async () => {
    if (!companyId) return;
    setAutopilotLoading(true);
    try { const { data } = await axios.get(`${API}/api/autopilot/settings/${companyId}`); setAutopilotSettings(data); }
    catch { setAutopilotSettings(null); } finally { setAutopilotLoading(false); }
  }, [companyId]);

  const updateAutopilot = async (key, value) => {
    if (!companyId) return;
    try { await axios.put(`${API}/api/autopilot/settings/${companyId}`, { [key]: value }); setAutopilotSettings((s) => ({ ...(s || {}), [key]: value })); } catch { /* silent */ }
  };

  // ─── Open handler ───
  const onOpen = useCallback((ev) => {
    const d = ev?.detail || {};
    const r = d.role || null;
    setRole(r);
    setInitialData(d.data || null);
    setCustomerToken(d.customerToken || null);
    setCustomerSlug(d.customerSlug || null);
    setSaSessionId(d.sessionId || null);
    setTab(d.tab || 'chat');
    setOpen(true);
    setDeckOpen(false);
    // Fresh chat on open; history available via the list toggle.
    setActiveId(null); setMessages([]); setPendingFiles([]); setQuestion(''); setShowList(false);
  }, []);

  useEffect(() => {
    window.addEventListener('ari:open-command-center', onOpen);
    return () => window.removeEventListener('ari:open-command-center', onOpen);
  }, [onOpen]);

  useEffect(() => { if (open) loadConversations(); }, [open, loadConversations]);
  useEffect(() => { if (open && tab === 'control' && isSuperAdmin && !companyId) loadAutomations(); }, [open, tab, isSuperAdmin, companyId, loadAutomations]);
  useEffect(() => {
    if (!open) return;
    if (tab === 'insights' && !briefing && showInsights) fetchBriefing(false);
    if (tab === 'control' && !autopilotSettings) fetchAutopilot();
  }, [open, tab, briefing, autopilotSettings, fetchBriefing, fetchAutopilot, showInsights]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages.length]);

  const onClose = () => setOpen(false);
  const goto = (route) => { if (!route) return; setOpen(false); try { navigate(route); } catch { /* ignore */ } };

  if (!open) return null;

  const state = briefing?.state || initialData?.state || {};
  const alerts = briefing?.alerts || initialData?.alerts || [];
  const suggestions = briefing?.suggestions || initialData?.suggestions || [];
  const summary = briefing?.summary || initialData?.summary || {};

  // Collapsed greeting — one clean sentence, no chips (owner-locked 2026-07
  // carousel revamp; same spec as the provider briefing).
  const userName = typeof window !== 'undefined' ? (localStorage.getItem('user_name') || '') : '';
  const firstName = userName.split(' ')[0] || '';
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const jobsToday = state.jobs?.today_total ?? summary.jobs_created_24h ?? 0;
  const greetingText = isSuperAdmin
    ? `${timeGreeting}${firstName ? `, ${firstName}` : ''}. Here's your platform pulse.`
    : `${timeGreeting}${firstName ? `, ${firstName}` : ''}. ${jobsToday === 0 ? 'Schedule is clear today.' : `${jobsToday} job${jobsToday !== 1 ? 's' : ''} on the board.`}`;

  // ── CARD DECK — actionable first (severity-sorted), informational trailing.
  const deck = [];
  [...alerts]
    .sort((a, b) => (ALERT_RANK[a.severity] ?? 2) - (ALERT_RANK[b.severity] ?? 2))
    .forEach((a, i) => {
      deck.push({
        key: `alert-${a.key || i}`,
        icon: <AlertTriangle className="w-3.5 h-3.5 text-white" />,
        iconBg: a.severity === 'critical' ? 'bg-red-400/30' : a.severity === 'high' ? 'bg-orange-400/30' : 'bg-amber-400/30',
        title: a.title,
        message: a.detail,
        cta: a.cta_label || null,
        onAction: () => goto(a.cta_route),
      });
    });
  suggestions.slice(0, 4).forEach((s, i) => {
    deck.push({
      key: `suggestion-${s.type || i}`,
      icon: <Lightbulb className="w-3.5 h-3.5 text-white" />,
      iconBg: 'bg-white/20',
      title: 'Ari suggests',
      message: s.message,
      cta: s.cta_label || (s.cta_route ? 'Take a look' : null),
      onAction: () => goto(s.cta_route),
    });
  });
  if (briefing?.briefing || initialData?.headline) {
    deck.push({
      key: 'daily-narrative',
      body: (
        <div data-testid="ari-cc-briefing">
          <p className="text-[12px] font-medium text-white/80 mb-2">Here's what's happening:</p>
          <p className="text-[12px] text-white/90 leading-relaxed whitespace-pre-wrap">{briefing?.briefing || initialData?.headline}</p>
          {briefing?.cached && <p className="text-[10px] mt-2 text-white/50">cached · tap refresh for latest</p>}
        </div>
      ),
    });
  }
  if (briefing) {
    deck.push({
      key: 'summary',
      body: (
        <div data-testid="ari-cc-stats">
          <p className="text-[12px] font-medium text-white/80 mb-2">Last 24 hours:</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md bg-white/10 p-2">
              <div className="text-[9px] uppercase tracking-wide text-white/60">Jobs Created 24h</div>
              <div className="text-[13px] font-bold text-white">{summary.jobs_created_24h ?? state.jobs?.today_total ?? 0}</div>
            </div>
            <div className="rounded-md bg-emerald-400/15 p-2">
              <div className="text-[9px] uppercase tracking-wide text-emerald-200/90">Payments Collected</div>
              <div className="text-[13px] font-bold text-white">${Math.round(summary.payments_collected_total || state.revenue_24h || 0).toLocaleString()}</div>
            </div>
            <div className="rounded-md bg-amber-400/15 p-2">
              <div className="text-[9px] uppercase tracking-wide text-amber-200/90">Unscheduled</div>
              <div className="text-[13px] font-bold text-white">{summary.jobs_unscheduled ?? state.jobs?.unscheduled ?? 0}</div>
            </div>
            <div className="rounded-md bg-red-400/15 p-2">
              <div className="text-[9px] uppercase tracking-wide text-red-200/90">Overdue</div>
              <div className="text-[13px] font-bold text-white">{summary.jobs_overdue ?? state.jobs?.overdue ?? 0}</div>
            </div>
          </div>
        </div>
      ),
    });
  }

  const tabs = [
    ...(showInsights ? [{ id: 'insights', label: 'Insights', icon: BarChart2 }] : []),
    { id: 'chat', label: 'Chat', icon: MessageSquare },
    ...(showControl ? [{ id: 'control', label: 'Control Center', icon: SettingsIcon }] : []),
  ];

  const activeTitle = conversations.find((c) => c.id === activeId)?.title || 'New chat';

  return (
    <div
      className="fixed inset-0 z-[9800] flex items-end md:items-center justify-center md:justify-end bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ari-cc-drawer-backdrop"
    >
      <div
        className="w-full md:w-[440px] md:mr-6 md:max-h-[85vh] bg-white dark:bg-slate-900 rounded-t-3xl md:rounded-2xl shadow-2xl flex flex-col border border-slate-200 dark:border-slate-800 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '85vh' }}
        data-testid="ari-cc-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800" style={{ background: 'var(--ari-primary-soft, #f5f3ff)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full flex items-center justify-center shadow-sm" style={{ background: 'var(--ari-primary, #7c3aed)' }}>
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Ari</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {tab === 'insights' && 'Last 24 hours'}
                {tab === 'chat' && (showList ? 'Your conversations' : 'Ask anything')}
                {tab === 'control' && 'System controls'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {tab === 'insights' && (
              <button onClick={() => fetchBriefing(true)} disabled={refreshing} data-testid="ari-cc-refresh"
                className="p-2 rounded-lg hover:bg-white/60 dark:hover:bg-slate-800 transition-colors" aria-label="Refresh briefing">
                <RefreshCw className={`w-4 h-4 text-slate-600 dark:text-slate-300 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button type="button" onClick={onClose} data-testid="ari-cc-close"
              className="p-2 rounded-lg hover:bg-white/60 dark:hover:bg-slate-800 transition-colors" aria-label="Close">
              <X className="w-4 h-4 text-slate-600 dark:text-slate-300" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="ari-cc-tabs">
          {tabs.map((t) => {
            const Icon = t.icon; const isActive = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ari-cc-tab-${t.id}`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors"
                style={isActive ? { color: 'var(--ari-primary, #7c3aed)', borderBottom: '2px solid var(--ari-primary, #7c3aed)' } : { color: '#64748b' }}>
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>

        {/* ═══ INSIGHTS TAB — collapsed greeting + one-card carousel ═══ */}
        {tab === 'insights' && showInsights && (
          <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="ari-cc-panel-insights">
            {loading && !briefing ? (
              <div className="flex items-center justify-center py-10 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /><span className="text-sm">Ari is preparing your briefing…</span>
              </div>
            ) : briefing ? (
              <div
                className="rounded-2xl overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 40%, #5B21B6 100%)' }}
              >
                <div className="px-4 pt-4 pb-4">
                  <p className="text-[13px] text-white/95 leading-relaxed" data-testid="ari-cc-greeting">
                    {greetingText}
                    {!deckOpen && (
                      <button
                        type="button"
                        onClick={() => setDeckOpen(true)}
                        className="ml-1.5 inline-flex items-center gap-0.5 text-[12px] font-bold text-white hover:text-white/80 align-baseline"
                        data-testid="ari-cc-view-briefing"
                      >
                        View Briefing
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </p>
                  {deckOpen && (
                    <>
                      <div className="mt-3">
                        <AriBriefingCarousel cards={deck} />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDeckOpen(false)}
                        className="mt-2 inline-flex items-center gap-0.5 text-[11px] font-bold text-white/70 hover:text-white"
                        data-testid="ari-cc-hide-briefing"
                      >
                        Hide Briefing
                        <ChevronDown className="w-3.5 h-3.5 rotate-180" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (<p className="text-xs text-slate-500">No insights to show yet.</p>)}
          </div>
        )}

        {/* ═══ CHAT TAB (Unified Chat Center) ═══ */}
        {tab === 'chat' && (
          <div className="flex-1 flex flex-col min-h-0" data-testid="ari-cc-panel-chat">
            {/* Chat toolbar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
              <button onClick={() => setShowList((v) => !v)} data-testid="ari-cc-history-toggle"
                className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                {showList ? <ChevronLeft className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                {showList ? 'Back to chat' : 'History'}
              </button>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[45%]" title={activeTitle}>{activeTitle}</span>
              <button onClick={newChat} data-testid="ari-cc-new-chat"
                className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg text-white" style={{ background: 'var(--ari-primary, #7c3aed)' }}>
                <Plus className="w-3.5 h-3.5" /> New
              </button>
            </div>

            {/* Temporary workspace tip — pinned above the thread until the
                company uses Workspace Evolution once, then gone forever. */}
            {showWorkspaceTip && !showList && (
              <div
                className="flex items-start gap-2 px-4 py-2.5 border-b border-violet-100 dark:border-violet-900/40 bg-violet-50/80 dark:bg-violet-950/30"
                data-testid="ari-workspace-tip"
              >
                <Lightbulb className="w-3.5 h-3.5 text-violet-600 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] leading-snug text-violet-900 dark:text-violet-200">
                  <span className="font-semibold">Tip:</span> You can ask me to reorganize your
                  workspace at any time — &ldquo;Move my calendar to the home screen&rdquo;,
                  &ldquo;Rename Customers to Clients&rdquo;, &ldquo;I don&rsquo;t need the map.&rdquo;
                </p>
              </div>
            )}

            {showList ? (
              /* Conversation list (pinned first) */
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5" data-testid="ari-cc-conversation-list">
                {conversations.length === 0 && (<p className="text-sm text-slate-400 text-center py-8">No conversations yet. Start a new chat.</p>)}
                {conversations.map((c) => (
                  <div key={c.id} data-testid={`ari-cc-conv-${c.id}`}
                    className={`group flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${c.id === activeId ? 'border-violet-300 bg-violet-50 dark:bg-violet-950/30' : 'border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}
                    onClick={() => openConversation(c.id)}>
                    {c.pinned && <Pin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--ari-primary, #7c3aed)' }} />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{c.title}</p>
                      {c.last_message_preview && <p className="text-xs text-slate-400 truncate">{c.last_message_preview}</p>}
                    </div>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); pinToggle(c.id, c.pinned); }} data-testid={`ari-cc-pin-${c.id}`}
                        className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" aria-label={c.pinned ? 'Unpin' : 'Pin'}>
                        {c.pinned ? <PinOff className="w-3.5 h-3.5 text-slate-500" /> : <Pin className="w-3.5 h-3.5 text-slate-500" />}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); archiveConv(c.id); }} data-testid={`ari-cc-archive-${c.id}`}
                        className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Archive">
                        <Archive className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }} data-testid={`ari-cc-delete-${c.id}`}
                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40" aria-label="Delete">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Message thread */
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {messages.length === 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-2">Ask Ari</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestionQuestions.map((s) => (
                        <button key={s} onClick={() => setQuestion(s)} data-testid={`ari-cc-q-${s.slice(0, 12)}`}
                          className="text-xs px-2.5 py-1.5 rounded-full border transition-colors"
                          style={{ borderColor: 'var(--ari-primary-ring, #c4b5fd)', background: 'var(--ari-primary-soft, #f5f3ff)', color: 'var(--ari-primary, #7c3aed)' }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((item, idx) => (
                  <div key={idx} data-testid={`ari-cc-msg-${idx}`} className={`flex flex-col ${item.role === 'user' ? 'items-end' : 'items-start'}`}>
                    {editingIdx === idx ? (
                      <div className="w-full max-w-[85%]" data-testid={`ari-cc-edit-box-${idx}`}>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={3}
                          autoFocus
                          data-testid={`ari-cc-edit-input-${idx}`}
                          className="w-full text-sm px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        />
                        <div className="flex justify-end gap-2 mt-1">
                          <button type="button" onClick={cancelEdit} data-testid={`ari-cc-edit-cancel-${idx}`}
                            className="text-xs px-3 py-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">Cancel</button>
                          <button type="button" onClick={saveEdit} disabled={!editText.trim() || sending} data-testid={`ari-cc-edit-save-${idx}`}
                            className="text-xs px-3 py-1.5 rounded-lg text-white disabled:opacity-50" style={{ background: 'var(--ari-primary, #7c3aed)' }}>
                            Save &amp; Regenerate
                          </button>
                        </div>
                      </div>
                    ) : (
                    <div className={item.role === 'user'
                      ? 'max-w-[85%] text-white text-sm px-3.5 py-2 rounded-2xl rounded-br-md'
                      : 'max-w-[90%] bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm px-3.5 py-2 rounded-2xl rounded-bl-md whitespace-pre-wrap'}
                      style={item.role === 'user' ? { background: 'var(--ari-primary, #7c3aed)' } : {}}>
                      {item.attachments && item.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          {item.attachments.map((a, ai) => a.kind === 'image' ? (
                            <a key={ai} href={`${API}${a.url}`} target="_blank" rel="noreferrer" className="block">
                              <img src={`${API}${a.url}`} alt={a.filename} className="w-16 h-16 object-cover rounded-lg border border-white/30" />
                            </a>
                          ) : (
                            <a key={ai} href={`${API}${a.url}`} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-white/20">
                              <FileText className="w-3.5 h-3.5" /> <span className="truncate max-w-[120px]">{a.filename}</span>
                            </a>
                          ))}
                        </div>
                      )}
                      {item.loading ? (
                        <span className="flex items-center gap-2 text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</span>
                      ) : (item.content || (item.attachments?.length ? '' : ''))}
                    </div>
                    )}
                    {item.role === 'assistant' && !item.loading && item.content && (
                      <span className="inline-flex items-center gap-1">
                        <CopyMessageButton text={item.content} testId={`ari-cc-copy-${idx}`} />
                        <button type="button" onClick={() => speakMessage(idx, item.content)}
                          data-testid={`ari-cc-speak-${idx}`} aria-label="Read aloud"
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-1 rounded-md text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 transition-colors">
                          {speakingIdx === idx ? <Square className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                          {speakingIdx === idx ? 'Stop' : 'Listen'}
                        </button>
                      </span>
                    )}
                    {item.role === 'user' && item.id && editingIdx !== idx && !sending && (
                      <button type="button" onClick={() => startEdit(idx)} data-testid={`ari-cc-edit-${idx}`}
                        aria-label="Edit message"
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-1 rounded-md text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 transition-colors">
                        <Pencil className="w-3 h-3" /> Edit{item.edited ? 'ed' : ''}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ CONTROL CENTER TAB ═══ */}
        {tab === 'control' && showControl && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="ari-cc-panel-control">
            {isSuperAdmin && !companyId ? (
              <>
              <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4" style={{ color: 'var(--ari-primary, #7c3aed)' }} /><span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Platform Controls</span></div>
                <p className="text-[13px] text-slate-600 dark:text-slate-300 leading-relaxed">Platform-wide Ari controls (policies, kill-switches, autonomy) live in the Super Admin dashboard.</p>
                <button onClick={() => goto('/super-admin')} data-testid="ari-cc-sa-controls-link"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg text-white" style={{ background: 'var(--ari-primary, #7c3aed)' }}>
                  Open Super Admin <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4" data-testid="ari-cc-automations">
                <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4" style={{ color: 'var(--ari-primary, #7c3aed)' }} /><span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Automations</span><span className="text-xs text-slate-400">{automations.length}</span></div>
                {automations.length === 0 ? (
                  <p className="text-[13px] text-slate-500 dark:text-slate-400 leading-relaxed">No automations yet. Tell me in chat, e.g. “Every Monday at 9am, find new appliance repair businesses within 25 miles of Akron and queue them for my approval.”</p>
                ) : (
                  <div className="space-y-2">
                    {automations.map((a) => (
                      <div key={a.id} className="rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2" data-testid={`ari-cc-automation-${a.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-slate-800 dark:text-slate-100 truncate">{a.name}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">{a.schedule_label} · <span className={a.status === 'active' ? 'text-emerald-500' : 'text-amber-500'}>{a.status}</span> · runs: {a.run_count || 0}{a.last_result ? ` · last: ${a.last_result.ok ? 'OK' : 'FAILED'}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => controlAutomation(a.id, a.status === 'active' ? 'pause' : 'resume')} disabled={autoBusy === a.id}
                              data-testid={`ari-cc-auto-toggle-${a.id}`} aria-label={a.status === 'active' ? 'Pause' : 'Resume'}
                              className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800">
                              {a.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => controlAutomation(a.id, 'delete')} disabled={autoBusy === a.id}
                              data-testid={`ari-cc-auto-delete-${a.id}`} aria-label="Delete automation"
                              className="p-1.5 rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </>
            ) : autopilotLoading && !autopilotSettings ? (
              <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /><span className="text-sm">Loading controls…</span></div>
            ) : (
              <div className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="flex items-center gap-2 mb-3"><Shield className="w-4 h-4" style={{ color: 'var(--ari-primary, #7c3aed)' }} /><span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Ari Operating Mode</span></div>
                <div className="flex gap-2">
                  {[['off', 'Manual'], ['assist', 'Assist'], ['full', 'Autopilot']].map(([mode, label]) => {
                    const isActive = autopilotSettings?.autopilot_mode === mode;
                    return (
                      <button key={mode} onClick={() => updateAutopilot('autopilot_mode', mode)} data-testid={`ari-cc-autopilot-${mode}`}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-colors ${isActive ? 'text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}
                        style={isActive ? { background: 'var(--ari-primary, #7c3aed)' } : {}}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
                  {autopilotSettings?.autopilot_mode === 'off' && 'Manual — you operate. Ari still understands your business, answers and recommends, but never executes on her own.'}
                  {autopilotSettings?.autopilot_mode === 'assist' && 'Assist — Ari prepares actions; anything needing your authority waits for your approval.'}
                  {autopilotSettings?.autopilot_mode === 'full' && 'Autopilot — Ari runs authorized workflows automatically within your company rules; you supervise.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Composer — Chat + Insights tabs */}
        {(tab === 'chat' || (tab === 'insights' && showInsights)) && !showList && (
          <form onSubmit={handleSend} className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/80"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ari-cc-pending-files">
                {pendingFiles.map((f, i) => (
                  <div key={i} data-testid={`ari-cc-file-chip-${i}`} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    {f.type?.startsWith('image/') ? <img src={URL.createObjectURL(f)} alt="" className="w-4 h-4 object-cover rounded" /> : <FileText className="w-3.5 h-3.5 text-slate-500" />}
                    <span className="truncate max-w-[110px]">{f.name}</span>
                    <button type="button" onClick={() => removePendingFile(i)} className="text-slate-400 hover:text-red-500" aria-label="Remove"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={onPickFiles} className="hidden" data-testid="ari-cc-camera-input" />
              <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" data-testid="ari-cc-photo-input" />
              <input ref={fileInputRef} type="file" accept={ACCEPT} multiple onChange={onPickFiles} className="hidden" data-testid="ari-cc-file-input" />
              <div className="relative flex-shrink-0">
                {attachMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-[60]" onClick={() => setAttachMenuOpen(false)} data-testid="ari-cc-attach-backdrop" />
                    <div className="absolute bottom-12 left-0 z-[61] w-48 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl py-1" data-testid="ari-cc-attach-menu">
                      <button type="button" data-testid="ari-cc-attach-camera"
                        onClick={() => { setAttachMenuOpen(false); cameraInputRef.current?.click(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60">
                        <Camera className="w-4 h-4 text-slate-500" /> Take photo
                      </button>
                      <button type="button" data-testid="ari-cc-attach-photo"
                        onClick={() => { setAttachMenuOpen(false); photoInputRef.current?.click(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60">
                        <ImageIcon className="w-4 h-4 text-slate-500" /> Choose photo
                      </button>
                      <button type="button" data-testid="ari-cc-attach-file"
                        onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60">
                        <FileText className="w-4 h-4 text-slate-500" /> Choose file
                      </button>
                    </div>
                  </>
                )}
                <button type="button" onClick={() => setAttachMenuOpen((v) => !v)} data-testid="ari-cc-attach"
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 hover:text-violet-600 transition-colors" aria-label="Attach">
                  <Paperclip className="w-4 h-4" />
                </button>
              </div>
              <button type="button" onClick={toggleRecording} data-testid="ari-cc-mic"
                aria-label={recording ? 'Stop recording' : 'Record voice note'}
                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${
                  recording
                    ? 'border-red-400 bg-red-50 dark:bg-red-950/40 text-red-600 animate-pulse'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 hover:text-violet-600'
                }`}>
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <textarea ref={inputRef} value={question}
                onChange={(e) => { setQuestion(e.target.value); autoGrowInput(e.target); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                onPaste={(e) => {
                  const el = e.target;
                  const pasted = (e.clipboardData || window.clipboardData).getData('text') || '';
                  const selStart = el.selectionStart ?? question.length;
                  const selEnd = el.selectionEnd ?? question.length;
                  const next = question.slice(0, selStart) + pasted + question.slice(selEnd);
                  if (next.length > MAX_INPUT) {
                    e.preventDefault();
                    setQuestion(next.slice(0, MAX_INPUT));
                    toast.warning(`Pasted text was trimmed to ${MAX_INPUT.toLocaleString()} characters`);
                    requestAnimationFrame(() => autoGrowInput(el));
                  }
                }}
                placeholder="Ask Ari anything…" data-testid="ari-cc-input" rows={1}
                className="flex-1 resize-none max-h-[120px] text-sm px-3.5 py-2.5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 leading-snug"
                style={{ '--tw-ring-color': 'var(--ari-primary-ring, #c4b5fd)' }} maxLength={MAX_INPUT} autoFocus />
              <Button type="submit" disabled={(!question.trim() && pendingFiles.length === 0) || sending} data-testid="ari-cc-send"
                className="rounded-full w-10 h-10 p-0 text-white flex-shrink-0" style={{ background: 'var(--ari-primary, #7c3aed)' }} aria-label="Send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            {question.length > 0 && (
              <div className="text-[10px] text-slate-400 dark:text-slate-500 text-right mt-1" data-testid="ari-cc-char-count">
                {question.length.toLocaleString()} / {MAX_INPUT.toLocaleString()}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default AriCommandCenterDrawer;
